import { useEffect, useState } from 'react';
import {
  getMyAccess,
  getMyTools,
  getPendingAccessRequests,
  PendingAccessRequest,
} from '../lib/api-client';

interface Stats {
  agentCount: number;
  resourceCount: number;
  toolCount: number;
}

export const Overview = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingRequests, setPendingRequests] = useState<PendingAccessRequest[] | null>(null);
  const [pendingError, setPendingError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    Promise.all([getMyAccess(), getMyTools()])
      .then(([access, tools]) => {
        if (cancelled) return;

        const resourceCount = access.agents.reduce((sum, a) => sum + a.resources.length, 0);
        const toolCount = tools.agents.reduce(
          (sum, a) => sum + a.resources.reduce((s, r) => s + r.tools.length, 0),
          0
        );

        setStats({
          agentCount: access.agents.length,
          resourceCount,
          toolCount,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load your overview');
        }
      });

    // Kept separate from the stats load above — a hiccup fetching pending
    // requests (e.g. the service app missing its admin-role assignment)
    // shouldn't take down the rest of the Overview page.
    getPendingAccessRequests()
      .then((response) => {
        if (!cancelled) {
          setPendingRequests(response.requests);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPendingError('Failed to load pending access requests');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <h1 className="page-title">Overview</h1>
      {error ? (
        <p className="error">{error}</p>
      ) : stats === null ? (
        <div className="centered">
          <div className="spinner" />
          <p className="muted">Loading overview...</p>
        </div>
      ) : (
        <div className="stat-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.agentCount}</div>
            <div className="stat-label">Agents you can access</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.resourceCount}</div>
            <div className="stat-label">Connected resources</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.toolCount}</div>
            <div className="stat-label">Tools available to you</div>
          </div>
        </div>
      )}

      <h2 className="page-title" style={{ fontSize: '1.1rem', marginTop: '2rem' }}>
        Pending Access Requests
      </h2>
      {pendingError ? (
        <p className="muted">{pendingError}</p>
      ) : pendingRequests === null ? (
        <div className="centered">
          <div className="spinner" />
        </div>
      ) : pendingRequests.length === 0 ? (
        <p className="muted">You have no pending access requests.</p>
      ) : (
        <ul className="agent-list">
          {pendingRequests.map((req) => (
            <li key={req.agent_id} className="agent-card">
              <div className="resource-row">
                <span className="resource-info">
                  <span className="resource-name">{req.agent_name}</span>
                </span>
                <div className="owner-actions">
                  <span className="status-pill">Pending</span>
                  {req.request_url ? (
                    <a
                      className="resource-action"
                      href={req.request_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View in Okta
                    </a>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
